import { useState, useCallback, useRef, useEffect, forwardRef, useImperativeHandle } from 'react';
import GraphPaper from './GraphPaper';
import Controls from './Controls';
import MobileToolbar from './MobileToolbar';

const PAGE_SIZES = [
  { id: 'letter', label: 'Letter', width: 816, height: 1056 },
  { id: 'a4', label: 'A4', width: 794, height: 1123 },
  { id: '11x17', label: '11×17', width: 1056, height: 1632 },
  { id: 'legal', label: 'Legal', width: 816, height: 1344 },
  { id: 'a3', label: 'A3', width: 1123, height: 1587 },
  { id: 'a2', label: 'A2', width: 1587, height: 2245 },
  { id: 'poster', label: 'Poster', width: 2304, height: 3456 },
  { id: 'movie-poster', label: 'Movie Poster', width: 2592, height: 3840 },
];

const DEFAULT_SETTINGS = {
  type: 'square',
  spacing: 5,
  minorWidth: 0.3,
  majorWidth: 0.8,
  color: '#1a1a1a',
  pageSize: 'letter',
  orientation: 'portrait',
  accentEvery: 0,
  marginLeft: 25,
};

function resolvePage(settings) {
  const page = PAGE_SIZES.find((p) => p.id === settings.pageSize) || PAGE_SIZES[0];
  const w = settings.orientation === 'landscape' ? page.height : page.width;
  const h = settings.orientation === 'landscape' ? page.width : page.height;
  return { page, width: w, height: h };
}

function serializeSvg(svgEl) {
  const clone = svgEl.cloneNode(true);
  clone.setAttribute('xmlns', 'http://www.w3.org/2000/svg');
  return new XMLSerializer()
    .serializeToString(clone)
    .replace(/var\(\s*--[\w-]+\s*,\s*([^)]+)\)/g, '$1');
}

const GraphPaperTool = forwardRef(function GraphPaperTool({ initialSettings = {} }, ref) {
  const [settings, setSettings] = useState({ ...DEFAULT_SETTINGS, ...initialSettings });
  const [isPrinting, setIsPrinting] = useState(false);
  const [previewRatio, setPreviewRatio] = useState(0.55);
  const [mobileDownloadOpen, setMobileDownloadOpen] = useState(false);
  const mobileDownloadRef = useRef(null);
  const printRef = useRef(null);
  const appBodyRef = useRef(null);
  const [scale, setScale] = useState(0.6);

  const handleDragStart = useCallback((e) => {
    e.preventDefault();
    const appBody = appBodyRef.current;
    if (!appBody) return;
    const startY = e.clientY;
    const rect = appBody.getBoundingClientRect();
    const startRatio = previewRatio;

    function onMove(e) {
      const dy = e.clientY - startY;
      const total = rect.height;
      let newRatio = Math.max(0, Math.min(0.85, startRatio - dy / total));
      setPreviewRatio(newRatio);
    }

    function onUp() {
      document.removeEventListener('pointermove', onMove);
      document.removeEventListener('pointerup', onUp);
    }

    document.addEventListener('pointermove', onMove);
    document.addEventListener('pointerup', onUp);
  }, [previewRatio]);

  const { page, width: pageWidth, height: pageHeight } = resolvePage(settings);

  useEffect(() => {
    function updateScale() {
      const isMobile = window.innerWidth < 768;
      const sidebarW = isMobile ? 0 : 320;
      const navH = 48;
      const toolbarH = isMobile ? 140 : 0;
      const gap = isMobile ? 16 : 80;
      const maxW = (window.innerWidth - sidebarW - gap) / pageWidth;
      const maxH = (window.innerHeight - navH - toolbarH - gap) / pageHeight;
      setScale(Math.min(maxW, maxH, 0.6));
    }
    updateScale();
    window.addEventListener('resize', updateScale);
    return () => window.removeEventListener('resize', updateScale);
  }, [pageWidth, pageHeight]);

  const updateSetting = useCallback((key, value) => {
    setSettings((prev) => ({ ...prev, [key]: value }));
  }, []);

  const handlePrint = useCallback(() => {
    setIsPrinting(true);
    requestAnimationFrame(() => {
      setTimeout(() => {
        window.print();
        setTimeout(() => setIsPrinting(false), 500);
      }, 150);
    });
  }, []);

  const handleDownload = useCallback((format) => {
    const svg = printRef.current?.querySelector('svg');
    if (!svg) return;

    if (format === 'svg') {
      const data = serializeSvg(svg);
      const blob = new Blob([data], { type: 'image/svg+xml;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.download = `graph-paper-${settings.type}-${settings.spacing}mm.svg`;
      link.href = url;
      link.click();
      URL.revokeObjectURL(url);
      return;
    }

    if (format === 'png') {
      const svgData = serializeSvg(svg);
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      const img = new Image();
      const dpr = 2;
      canvas.width = pageWidth * dpr;
      canvas.height = pageHeight * dpr;
      const blob = new Blob([svgData], { type: 'image/svg+xml;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      img.onload = () => {
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        URL.revokeObjectURL(url);
        const link = document.createElement('a');
        link.download = `graph-paper-${settings.type}-${settings.spacing}mm.png`;
        link.href = canvas.toDataURL('image/png');
        link.click();
      };
      img.src = url;
      return;
    }

    if (format === 'pdf') {
      setIsPrinting(true);
      requestAnimationFrame(() => {
        setTimeout(() => {
          window.print();
          setTimeout(() => setIsPrinting(false), 500);
        }, 150);
      });
    }
  }, [settings, pageWidth, pageHeight]);

  useImperativeHandle(ref, () => ({ handlePrint, handleDownload }), [handlePrint, handleDownload]);

  useEffect(() => {
    function handleClick(e) {
      if (mobileDownloadRef.current && !mobileDownloadRef.current.contains(e.target)) {
        setMobileDownloadOpen(false);
      }
    }
    if (mobileDownloadOpen) {
      document.addEventListener('mousedown', handleClick);
    }
    return () => document.removeEventListener('mousedown', handleClick);
  }, [mobileDownloadOpen]);

  return (
    <div className={`tool-root ${isPrinting ? 'is-printing' : ''}`}>
      <div className="app-body" ref={appBodyRef} style={{ '--preview-ratio': previewRatio }}>
        <Controls
          settings={settings}
          onChange={updateSetting}
          onPrint={handlePrint}
          onDownload={handleDownload}
        />

        <div className="preview-drag-handle" onPointerDown={handleDragStart}>
          <div className="preview-drag-grip">
            <span></span><span></span><span></span><span></span>
          </div>
        </div>

        <main className="preview-area">
          <div className="preview-header">
            <span className="preview-label">
              {page.label} · {settings.orientation}
              <span className="preview-dimensions">{(pageWidth / 96).toFixed(1)}×{(pageHeight / 96).toFixed(1)}″</span>
            </span>
          </div>
          <div className="preview-scroll">
            <div className="preview-page" style={{ width: pageWidth * scale, height: pageHeight * scale }}>
              <div style={{ transform: `scale(${scale})`, transformOrigin: 'top left', width: pageWidth, height: pageHeight }}>
                <GraphPaper
                  type={settings.type}
                  spacing={settings.spacing}
                  minorWidth={settings.minorWidth}
                  majorWidth={settings.majorWidth}
                  color={settings.color}
                  width={pageWidth}
                  height={pageHeight}
                  accentEvery={settings.accentEvery}
                  marginLeft={settings.type === 'lined' ? settings.marginLeft : 0}
                />
              </div>
            </div>
          </div>
          <div className="preview-footer">
            <span className="preview-hint">Ctrl+P to print · Download as PDF, PNG, or SVG</span>
          </div>
        </main>
      </div>

      <MobileToolbar
        settings={settings}
        onChange={updateSetting}
        onPrint={handlePrint}
        onDownload={handleDownload}
      />

      <div className="print-sheet" ref={printRef}>
        <GraphPaper
          type={settings.type}
          spacing={settings.spacing}
          minorWidth={settings.minorWidth}
          majorWidth={settings.majorWidth}
          color={settings.color}
          width={pageWidth}
          height={pageHeight}
          accentEvery={settings.accentEvery}
          marginLeft={settings.type === 'lined' ? settings.marginLeft : 0}
        />
      </div>
    </div>
  );
});

export default GraphPaperTool;